/**
 * SubscriptionPage Component
 *
 * Page de gestion d'abonnement permettant aux utilisateurs de:
 * - Voir leur statut d'abonnement actuel (Free/Premium)
 * - Mettre à niveau vers Premium (pour les utilisateurs Free)
 * - Voir la date de renouvellement et annuler (pour les utilisateurs Premium)
 * - Voir l'historique de leurs achats
 *
 * @validates Requirements 9.1, 9.2, 9.5, 9.6, 9.7, 9.8, 5.6
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '../store';
import { UpgradeModal } from '../components/subscription';
import { useTranslation } from '../hooks/useTranslation';

/**
 * Subscription status from API
 */
interface SubscriptionStatus {
  userId: string;
  type: 'free' | 'premium';
  status: 'active' | 'cancelled' | 'expired';
  memberSince: string;
  subscriptionStartDate: string | null;
  endsAt: string | null;
  renewalDate: string | null;
  expirationDate: string | null;
  isPremium: boolean;
  isAdmin: boolean;
  paymentType: 'one-time' | 'subscription' | null;
  isRecurring: boolean;
  canCancel: boolean;
  hasActiveSubscription: boolean;
  isCancelled?: boolean;
}

/**
 * Quota status from API
 */
interface QuotaData {
  remaining: number;
  limit: number;
  used: number;
  resetAt: string;
  isPremium: boolean;
  isAdmin: boolean;
  isUnlimited: boolean;
  exceeded: boolean;
}

/**
 * Cancel response from API
 */
interface CancelResponse {
  message: string;
  effectiveEndDate: string | null;
}

/**
 * Format a date string to locale-appropriate format
 */
function formatDate(dateString: string | null, locale: string = 'fr'): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString(locale === 'fr' ? 'fr-FR' : 'en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function SubscriptionPage() {
  const { token, isAuthenticated, isPremium } = useStore();
  const { t, currentLanguage } = useTranslation();
  const freeUserDailyQuota = useStore((state) => state.freeUserDailyQuota);
  const premiumMonthlyPrice = useStore((state) => state.premiumMonthlyPrice);
  const premiumSubscriptionPrice = useStore((state) => state.premiumSubscriptionPrice);
  const loadPublicConfig = useStore((state) => state.loadPublicConfig);

  // Calculate savings for annual subscription vs one-time payment
  // Annual subscription price (monthly × 12)
  const annualSubscriptionPrice = premiumSubscriptionPrice * 12;
  // Yearly cost if paying one-time each month
  const yearlyOneTimeCost = premiumMonthlyPrice * 12;
  // Savings per year
  const yearlySavings = yearlyOneTimeCost - annualSubscriptionPrice;
  const savingsPercent = yearlyOneTimeCost > 0 
    ? Math.round((yearlySavings / yearlyOneTimeCost) * 100)
    : 0;

  // State
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [quotaData, setQuotaData] = useState<QuotaData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState<CancelResponse | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  /**
   * Fetch subscription status from API
   */
  const fetchSubscriptionStatus = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch('/api/subscription/status', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(t('subscription.errors.fetchStatus'));
      }

      const data = await response.json();
      if (data.success) {
        setSubscriptionStatus(data.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    }
  }, [token, t]);

  /**
   * Fetch quota status from API
   */
  const fetchQuotaStatus = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch('/api/subscription/quota', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(t('subscription.errors.fetchQuota'));
      }

      const data = await response.json();
      if (data.success) {
        setQuotaData(data.data.quota);
      }
    } catch (err) {
      // Quota errors are non-critical, don't block the page
      console.error('Quota error:', err);
    }
  }, [token, t]);

  /**
   * Load data on mount
   */
  useEffect(() => {
    async function loadData() {
      setIsLoading(true);
      setError(null);
      // Load public config to get current price
      await loadPublicConfig(true);
      await Promise.all([fetchSubscriptionStatus(), fetchQuotaStatus()]);
      setIsLoading(false);
    }

    if (isAuthenticated) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated, fetchSubscriptionStatus, fetchQuotaStatus, loadPublicConfig]);

  /**
   * Handle subscription cancellation
   */
  const handleCancelSubscription = async () => {
    if (!token) return;

    setIsCancelling(true);
    setError(null);

    try {
      const response = await fetch('/api/subscription/cancel', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || t('subscription.errors.cancel'));
      }

      if (data.success) {
        setCancelSuccess(data.data);
        setCancelConfirmOpen(false);
        // Refresh subscription status
        await fetchSubscriptionStatus();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setIsCancelling(false);
    }
  };

  // Determine if user is premium based on store or fetched status
  const userIsPremium = isPremium() || subscriptionStatus?.isPremium || subscriptionStatus?.isAdmin;

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-white mb-4">
          {t('subscription.title')}
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mb-6">
          {t('subscription.loginRequired.message')}
        </p>
        <Link
          to="/login"
          className="btn-primary inline-block"
        >
          {t('subscription.loginRequired.login')}
        </Link>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-12">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-1/3"></div>
          <div className="h-48 bg-slate-200 dark:bg-slate-700 rounded-2xl"></div>
          <div className="h-32 bg-slate-200 dark:bg-slate-700 rounded-2xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6">
      <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-white mb-8">
        {t('subscription.title')}
      </h1>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400">
          <p className="font-medium">{t('common.error')}</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Cancel Success Alert */}
      {cancelSuccess && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-green-700 dark:text-green-400">
          <p className="font-medium">{t('subscription.cancel.success.title')}</p>
          <p className="text-sm">{cancelSuccess.message}</p>
          {cancelSuccess.effectiveEndDate && (
            <p className="text-sm mt-1">
              {t('subscription.cancel.success.activeUntil')}{' '}
              <strong>{formatDate(cancelSuccess.effectiveEndDate, currentLanguage)}</strong>.
            </p>
          )}
        </div>
      )}

      {/* Cancelled Subscription Info Alert */}
      {subscriptionStatus?.status === 'cancelled' && subscriptionStatus?.endsAt && !cancelSuccess && (
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl text-amber-800 dark:text-amber-400">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 mt-0.5 text-amber-500 dark:text-amber-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="font-medium">{t('subscription.cancelled.title')}</p>
              <p className="text-sm mt-1">
                {t('subscription.cancelled.message').replace('{date}', formatDate(subscriptionStatus.endsAt, currentLanguage))}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Current Subscription Status Card */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
            <div className="flex items-start justify-between mb-4">
              <h2 className="font-semibold text-lg text-slate-900 dark:text-white">
                {t('subscription.status.title')}
              </h2>
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  userIsPremium
                    ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                }`}
              >
                {subscriptionStatus?.isAdmin
                  ? t('subscription.status.types.admin')
                  : userIsPremium
                  ? t('subscription.status.types.premium')
                  : t('subscription.status.types.free')}
              </span>
            </div>

            <div className="space-y-4">
              {/* Subscription Type */}
              <div>
                <label className="block text-sm text-slate-500 dark:text-slate-400 mb-1">
                  {t('subscription.status.subscriptionType.label')}
                </label>
                <p className="text-slate-900 dark:text-white font-medium">
                  {subscriptionStatus?.isAdmin
                    ? t('subscription.status.subscriptionType.admin')
                    : userIsPremium
                    ? (subscriptionStatus?.isRecurring 
                        ? t('subscription.status.subscriptionType.premiumAnnual')
                        : t('subscription.status.subscriptionType.premiumMonthly'))
                    : t('subscription.status.subscriptionType.free')}
                </p>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm text-slate-500 dark:text-slate-400 mb-1">
                  {t('subscription.status.statusLabel')}
                </label>
                <p className="text-slate-900 dark:text-white">
                  {subscriptionStatus?.status === 'active' && t('subscription.status.statusValues.active')}
                  {subscriptionStatus?.status === 'cancelled' && t('subscription.status.statusValues.cancelled')}
                  {subscriptionStatus?.status === 'expired' && t('subscription.status.statusValues.expired')}
                </p>
              </div>

              {/* Start Date (subscription start date for premium, member since for free) */}
              {userIsPremium && subscriptionStatus?.subscriptionStartDate && (
                <div>
                  <label className="block text-sm text-slate-500 dark:text-slate-400 mb-1">
                    {t('subscription.status.startDate')}
                  </label>
                  <p className="text-slate-900 dark:text-white">
                    {formatDate(subscriptionStatus.subscriptionStartDate, currentLanguage)}
                  </p>
                </div>
              )}
              {!userIsPremium && subscriptionStatus?.memberSince && (
                <div>
                  <label className="block text-sm text-slate-500 dark:text-slate-400 mb-1">
                    {t('subscription.status.memberSince')}
                  </label>
                  <p className="text-slate-900 dark:text-white">
                    {formatDate(subscriptionStatus.memberSince, currentLanguage)}
                  </p>
                </div>
              )}

              {/* Renewal Date (Recurring subscription only) */}
              {userIsPremium && subscriptionStatus?.isRecurring && subscriptionStatus?.renewalDate && subscriptionStatus.status === 'active' && (
                <div>
                  <label className="block text-sm text-slate-500 dark:text-slate-400 mb-1">
                    {t('subscription.status.renewalDate')}
                  </label>
                  <p className="text-slate-900 dark:text-white">
                    {formatDate(subscriptionStatus.renewalDate, currentLanguage)}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {t('subscription.status.renewalNote')}
                  </p>
                </div>
              )}

              {/* Expiration Date (One-time payment only) */}
              {userIsPremium && !subscriptionStatus?.isRecurring && subscriptionStatus?.expirationDate && subscriptionStatus.status === 'active' && (
                <div>
                  <label className="block text-sm text-slate-500 dark:text-slate-400 mb-1">
                    {t('subscription.status.expirationDate') ?? 'Date d\'expiration'}
                  </label>
                  <p className="text-slate-900 dark:text-white">
                    {formatDate(subscriptionStatus.expirationDate, currentLanguage)}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {t('subscription.status.expirationNote') ?? 'Votre abonnement ne sera pas renouvelé automatiquement.'}
                  </p>
                </div>
              )}

              {/* End Date (for cancelled subscriptions) */}
              {subscriptionStatus?.status === 'cancelled' && subscriptionStatus?.endsAt && (
                <div>
                  <label className="block text-sm text-slate-500 dark:text-slate-400 mb-1">
                    {t('subscription.status.endDate')}
                  </label>
                  <p className="text-slate-900 dark:text-white">
                    {formatDate(subscriptionStatus.endsAt, currentLanguage)}
                  </p>
                </div>
              )}
            </div>

            {/* Cancel Button (Recurring subscription only, not already cancelled, not admin) */}
            {userIsPremium &&
              !subscriptionStatus?.isAdmin &&
              subscriptionStatus?.canCancel &&
              subscriptionStatus?.status === 'active' && (
                <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-700">
                  {!cancelConfirmOpen ? (
                    <button
                      type="button"
                      onClick={() => setCancelConfirmOpen(true)}
                      className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                    >
                      {t('subscription.cancel.button')}
                    </button>
                  ) : (
                    <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-xl">
                      <p className="text-red-800 dark:text-red-400 font-medium mb-2">
                        {t('subscription.cancel.confirm.title')}
                      </p>
                      <p className="text-red-700 dark:text-red-400 text-sm mb-4">
                        {t('subscription.cancel.confirm.message')}
                      </p>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={handleCancelSubscription}
                          disabled={isCancelling}
                          className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isCancelling ? t('subscription.cancel.confirm.processing') : t('subscription.cancel.confirm.confirmButton')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setCancelConfirmOpen(false)}
                          disabled={isCancelling}
                          className="px-4 py-2 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 transition-colors"
                        >
                          {t('subscription.cancel.confirm.cancelButton')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
          </div>

          {/* Quota Information (Free users) */}
          {!userIsPremium && quotaData && (
            <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
              <h2 className="font-semibold text-lg text-slate-900 dark:text-white mb-4">
                {t('subscription.quota.title')}
              </h2>

              <div className="mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-600 dark:text-slate-400">{t('subscription.quota.usedToday')}</span>
                  <span className="font-medium text-slate-900 dark:text-white">
                    {quotaData.used} / {quotaData.limit === -1 ? t('subscription.quota.unlimited') : quotaData.limit}
                  </span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full transition-all duration-300 ${
                      quotaData.exceeded
                        ? 'bg-red-500'
                        : quotaData.remaining <= 5
                        ? 'bg-amber-500'
                        : 'bg-primary-500'
                    }`}
                    style={{
                      width: `${Math.min((quotaData.used / quotaData.limit) * 100, 100)}%`,
                    }}
                  ></div>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">
                  {quotaData.remaining > 0
                    ? (() => {
                        const parts = t('subscription.quota.remaining').split('|');
                        const template = parts[quotaData.remaining > 1 ? 1 : 0] ?? parts[0] ?? '';
                        return template.trim().replace('{count}', String(quotaData.remaining));
                      })()
                    : t('subscription.quota.limitReached')}
                </span>
                <span className="text-slate-500 dark:text-slate-500">
                  {t('subscription.quota.resetAt')}
                </span>
              </div>

              {quotaData.exceeded && (
                <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-amber-800 dark:text-amber-400 text-sm">
                  <p className="font-medium">{t('subscription.quota.exceeded.title')}</p>
                  <p>{t('subscription.quota.exceeded.message')}</p>
                </div>
              )}
            </div>
          )}

          {/* Features Comparison */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
            <h2 className="font-semibold text-lg text-slate-900 dark:text-white mb-4">
              {t('subscription.comparison.title')}
            </h2>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left py-3 pr-4 font-medium text-slate-900 dark:text-white">
                      {t('subscription.comparison.feature')}
                    </th>
                    <th className="text-center py-3 px-4 font-medium text-slate-900 dark:text-white">
                      {t('subscription.comparison.free')}
                    </th>
                    <th className="text-center py-3 pl-4 font-medium text-primary-600 dark:text-primary-400">
                      {t('subscription.comparison.premium')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  <tr>
                    <td className="py-3 pr-4 text-slate-700 dark:text-slate-300">
                      {t('subscription.comparison.features.searchesPerDay')}
                    </td>
                    <td className="py-3 px-4 text-center text-slate-600 dark:text-slate-400">{freeUserDailyQuota}</td>
                    <td className="py-3 pl-4 text-center text-primary-600 dark:text-primary-400 font-medium">
                      {t('subscription.comparison.values.unlimited')}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4 text-slate-700 dark:text-slate-300">
                      {t('subscription.comparison.features.dictionaryAccess')}
                    </td>
                    <td className="py-3 px-4 text-center text-slate-600 dark:text-slate-400">{t('subscription.comparison.values.defaultOnly')}</td>
                    <td className="py-3 pl-4 text-center text-primary-600 dark:text-primary-400 font-medium">
                      {t('subscription.comparison.values.allDictionaries')}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4 text-slate-700 dark:text-slate-300">
                      {t('subscription.comparison.features.customLists')}
                    </td>
                    <td className="py-3 px-4 text-center text-slate-600 dark:text-slate-400">{t('common.no')}</td>
                    <td className="py-3 pl-4 text-center text-primary-600 dark:text-primary-400 font-medium">
                      {t('subscription.comparison.values.upTo5')}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4 text-slate-700 dark:text-slate-300">
                      {t('subscription.comparison.features.exportDictionaries')}
                    </td>
                    <td className="py-3 px-4 text-center text-slate-600 dark:text-slate-400">{t('common.no')}</td>
                    <td className="py-3 pl-4 text-center text-primary-600 dark:text-primary-400 font-medium">
                      {t('common.yes')}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Purchase History Link */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-lg text-slate-900 dark:text-white">
                  Historique des achats
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Consultez tous vos achats et abonnements passés
                </p>
              </div>
              <Link
                to="/purchase-history"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-medium rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
              >
                Voir l'historique
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Upgrade Card (Free users only) */}
          {!userIsPremium && (
            <div className="bg-gradient-to-br from-primary-500 to-secondary-600 p-6 rounded-2xl text-white">
              <h2 className="font-semibold text-xl mb-2">{t('subscription.upgrade.title')}</h2>
              <p className="text-primary-100 text-sm mb-4">
                {t('subscription.upgrade.description')}
              </p>

              {/* Pricing Grid */}
              <div className="space-y-3 mb-4">
                {/* Annual subscription - Recommended */}
                <div className="relative bg-white/15 backdrop-blur rounded-xl p-4 border-2 border-white/30">
                  <div className="absolute -top-2 right-3">
                    <span className="px-2 py-0.5 bg-green-400 text-green-900 text-xs font-bold rounded-full">
                      RECOMMANDÉ
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">💎</span>
                    <span className="font-semibold">Abonnement annuel</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold">{annualSubscriptionPrice.toFixed(2).replace('.', ',')}€</span>
                    <span className="text-primary-100 text-sm">/an</span>
                  </div>
                  <div className="text-primary-100 text-xs mt-1">
                    soit {premiumSubscriptionPrice.toFixed(2).replace('.', ',')}€/mois
                  </div>
                  {savingsPercent > 0 && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-green-300">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Économisez {yearlySavings.toFixed(2).replace('.', ',')}€/an (-{savingsPercent}%)
                    </div>
                  )}
                </div>

                {/* Monthly payment */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">📅</span>
                    <span className="font-medium text-primary-100">Paiement mensuel</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-xl font-semibold text-primary-100">{premiumMonthlyPrice.toFixed(2).replace('.', ',')}€</span>
                    <span className="text-primary-200 text-sm">/mois</span>
                  </div>
                  <div className="text-primary-200 text-xs mt-1">
                    Renouvellement manuel
                  </div>
                </div>
              </div>

              <ul className="space-y-2 text-sm mb-5">
                <li className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-primary-200"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {t('subscription.upgrade.benefits.unlimited')}
                </li>
                <li className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-primary-200"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {t('subscription.upgrade.benefits.allDictionaries')}
                </li>
                <li className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-primary-200"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {t('subscription.upgrade.benefits.import')}
                </li>
              </ul>

              <button
                type="button"
                onClick={() => setShowUpgradeModal(true)}
                className="w-full py-3 bg-white text-primary-600 font-medium rounded-xl hover:bg-primary-50 transition-colors"
              >
                {t('subscription.upgrade.button')}
              </button>

              <p className="text-primary-200 text-xs text-center mt-3 whitespace-pre-line">
                {t('subscription.upgrade.note')}
              </p>
            </div>
          )}

          {/* Premium Active Card (Premium users) */}
          {userIsPremium && !subscriptionStatus?.isAdmin && (
            <div className="bg-gradient-to-br from-primary-500 to-secondary-600 p-6 rounded-2xl text-white">
              <div className="flex items-center gap-2 mb-3">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                  />
                </svg>
                <span className="font-semibold">{t('subscription.premiumActive.title')}</span>
              </div>
              <p className="text-primary-100 text-sm">
                {t('subscription.premiumActive.message')}
              </p>
            </div>
          )}

          {/* Admin Card */}
          {subscriptionStatus?.isAdmin && (
            <div className="bg-gradient-to-br from-slate-700 to-slate-900 p-6 rounded-2xl text-white">
              <div className="flex items-center gap-2 mb-3">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
                <span className="font-semibold">{t('subscription.adminCard.title')}</span>
              </div>
              <p className="text-slate-300 text-sm">
                {t('subscription.adminCard.message')}
              </p>
              <Link
                to="/admin"
                className="inline-flex items-center gap-1 mt-4 text-sm text-white hover:text-slate-200 transition-colors"
              >
                {t('subscription.adminCard.accessAdmin')}
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
            </div>
          )}

          {/* Quick Links */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
            <h2 className="font-semibold text-lg text-slate-900 dark:text-white mb-4">
              {t('subscription.quickLinks.title')}
            </h2>

            <div className="space-y-2">
              <Link
                to="/account"
                className="flex items-center gap-2 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                <span>👤</span>
                <span className="text-slate-700 dark:text-slate-300">{t('subscription.quickLinks.myAccount')}</span>
              </Link>
              <Link
                to="/purchase-history"
                className="flex items-center gap-2 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                <span>📜</span>
                <span className="text-slate-700 dark:text-slate-300">Historique des achats</span>
              </Link>
              <Link
                to="/"
                className="flex items-center gap-2 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                <span>🔍</span>
                <span className="text-slate-700 dark:text-slate-300">{t('subscription.quickLinks.newSearch')}</span>
              </Link>
              <a
                href="mailto:support@spellion.app"
                className="flex items-center gap-2 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                <span>📧</span>
                <span className="text-slate-700 dark:text-slate-300">{t('subscription.quickLinks.contactSupport')}</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onSuccess={() => {
          // Refresh subscription status after successful upgrade
          fetchSubscriptionStatus();
          fetchQuotaStatus();
        }}
      />
    </div>
  );
}
